import type { QueryView } from "~/models/dto";
import type { Time } from "~/tools/date-time/time";
import { Impulse } from "~/tools/impulse";
import type { Record } from "~/tools/record";

import type { TimeRange } from "../../../app/state/view/timeline/TimeRange";
import type { ServiceTokenDrawerState } from "../../../bottom_content/stackpacks/installation-guide/ServiceTokenDrawer";
import type { ComponentLastChangeDrawerState } from "../../../bottom_content/view/highlights-perspective/drawers/ComponentLastChangeDrawer";
import type { YamlDisplayDrawerState } from "../../../bottom_content/view/highlights-perspective/drawers/YamlDisplayDrawer";
import {
	CheckStateChain,
	type MonitorStatusDrawerState,
} from "../../../bottom_content/view/highlights-perspective/monitor-status-drawer/MonitorStatusDrawer";
import type { MonitorStatusDrawerTabType } from "../../../bottom_content/view/highlights-perspective/monitor-status-drawer/MonitorStatusDrawer/monitor-status-drawer-state";
import type { RelatedResourcesDrawerState } from "../../../bottom_content/view/highlights-perspective/related-resources-drawer/state";
import type { ElementPropertiesLogsDrawerState } from "../../../bottom_content/view/panes/east/element-details/element-details-drawer/ElementPropertiesLogsDrawer";
import type { EventDiffDrawerState } from "../../../bottom_content/view/panes/east/event-details/event-diff-drawer";
import type { FullComponent, FullRelation, LogSeverity, TopologyEvent } from "../../../common/openapi";
import { jsonToYaml } from "../../../generic/yamlUtils";
import type { AiAssistantDrawerState } from "../../../sessions/ai-assistant-drawer/ai-assistant-drawer";
import type { AddToDashboardDrawerState } from "../../_add-to-dashboard-drawer";
import type { DashboardVariablesDrawerState } from "../../_dashboard-variables-drawer";
import type { ErrorDetailsDrawerState } from "../../_drawer";
import type { MonitorOutputDrawerState } from "../../_monitor-output-drawer";
import type { MonitorPreviewDrawerState } from "../../_monitor-preview-drawer";
import type { SetupDashboardDrawerState } from "../../_setup-dashboard-drawer";
import {
	type InitSetupDashboardVariableCreateDrawerStateParams,
	type InitSetupDashboardVariableEditDrawerStateParams,
	type SetupDashboardVariableDrawerState,
	initSetupDashboardVariableCreateDrawerState,
	initSetupDashboardVariableEditDrawerState,
} from "../../_setup-dashboard-variable-drawer";
import {
	type InitCreateDashboardWidgetDrawerStateParams,
	type InitEditDashboardWidgetDrawerStateParams,
	type SetupDashboardWidgetDrawerState,
	initCreateDashboardWidgetDrawerState,
	initEditDashboardWidgetDrawerState,
} from "../../_setup-dashboard-widget-drawer/drawer-state";
import type { BoundMetricsDrawerState } from "../../metrics-drawer/bound/state";
import type { MetricsDrawerState } from "../../metrics-drawer/state";

export interface ExplorationDrawerTable {
	componentLastChange?: ComponentLastChangeDrawerState;
	yamlDisplay?: YamlDisplayDrawerState;
	monitorPreview?: MonitorPreviewDrawerState;
	componentLogs?: ElementPropertiesLogsDrawerState;
	eventDiff?: EventDiffDrawerState;
	metrics?: MetricsDrawerState;
	boundMetrics?: BoundMetricsDrawerState;
	monitorStatus?: MonitorStatusDrawerState;
	relatedResources?: RelatedResourcesDrawerState;
	monitorOutput?: MonitorOutputDrawerState;
	elementProperties?: FullComponent | FullRelation;
	elementJsonDefinition?: FullComponent | FullRelation;
	eventProperties?: TopologyEvent;
	errorDetails?: ErrorDetailsDrawerState;
	viewForm?: QueryView;
	setupDashboard?: SetupDashboardDrawerState;
	addToDashboard?: AddToDashboardDrawerState;
	setupDashboardWidget?: SetupDashboardWidgetDrawerState;
	dashboardVariables?: DashboardVariablesDrawerState;
	dashboardVariable?: SetupDashboardVariableDrawerState;
	serviceToken?: ServiceTokenDrawerState;
	aiAssistant?: AiAssistantDrawerState;
}

export class ExplorationDrawerState {
	public constructor(
		// the open function is limited to take only one key at a time, so that we can't
		// accidentally open multiple drawers at the same time
		private readonly open: <TKey extends keyof ExplorationDrawerTable>(
			key: TKey,
			state: ExplorationDrawerTable[TKey],
		) => void,
	) {}

	public openComponentLastChange({
		component,
		sourceProperties,
		lastUpdateTimestamp,
	}: Omit<ComponentLastChangeDrawerState, "sourceProperties"> & {
		sourceProperties: undefined | Record<string>;
	}): void {
		this.open("componentLastChange", {
			component,
			sourceProperties: sourceProperties && jsonToYaml(sourceProperties),
			lastUpdateTimestamp,
		});
	}

	public openComponentStatus({ component, status }: { component: FullComponent; status: Record<string> }): void {
		this.open("yamlDisplay", {
			type: "status",
			component,
			yaml: jsonToYaml(status),
		});
	}

	public openComponentConfiguration({
		component,
		configuration,
	}: {
		component: FullComponent;
		configuration: Record<string>;
	}): void {
		this.open("yamlDisplay", {
			type: "configuration",
			component,
			yaml: jsonToYaml(configuration),
		});
	}

	public openMonitorPreview({ title, monitorId, activeTab }: MonitorPreviewDrawerState): void {
		this.open("monitorPreview", {
			title,
			monitorId,
			activeTab,
		});
	}

	public openComponentLogs({
		component,
		timestamp,
		timeRange,
		severity,
	}: {
		component: FullComponent;
		timestamp?: Time;
		timeRange: TimeRange;
		severity?: LogSeverity;
	}): void {
		this.open("componentLogs", {
			component,
			timestamp,
			timeRange: Impulse(timeRange),
			severity,
		});
	}

	public openEventDiff({ elements, data }: TopologyEvent): void {
		const element = elements.at(0);

		if (element == null) {
			return;
		}

		this.open("eventDiff", {
			element,
			oldCode: jsonToYaml(data.old),
			newCode: jsonToYaml(data.new),
			oldTimestamp: data.oldTimestamp as Time,
			newTimestamp: data.newTimestamp as Time,
		});
	}

	public openMetrics(metrics: MetricsDrawerState): void {
		this.open("metrics", metrics);
	}

	public openBoundMetrics(boundMetrics: BoundMetricsDrawerState): void {
		this.open("boundMetrics", boundMetrics);
	}

	public openMonitorStatus({
		identifier,
		selectedTab,
		showImpactedComponent = false,
		timestamp,
		timeRange,
	}: {
		identifier: string;
		showImpactedComponent?: boolean;
		selectedTab?: MonitorStatusDrawerTabType;
		timestamp: Time;
		timeRange: TimeRange;
	}): void {
		this.open("monitorStatus", {
			checkStateChain: CheckStateChain.init({
				identifier,
				selectedTab: Impulse(selectedTab),
				showImpactedComponent: Impulse(showImpactedComponent),
			}),
			timestamp: Impulse(timestamp),
			timeRange: Impulse(timeRange),
		});
	}

	public openRelatedResources(state: RelatedResourcesDrawerState): void {
		this.open("relatedResources", state);
	}

	public openMonitorOutput(state: MonitorOutputDrawerState): void {
		this.open("monitorOutput", state);
	}

	public openElementProperties(state: FullComponent | FullRelation): void {
		this.open("elementProperties", state);
	}

	public openElementJsonDefinition(state: FullComponent | FullRelation): void {
		this.open("elementJsonDefinition", state);
	}

	public openEventProperties(state: TopologyEvent): void {
		this.open("eventProperties", state);
	}

	public openErrorDetails(state: ErrorDetailsDrawerState): void {
		this.open("errorDetails", state);
	}

	public openViewForm(state: QueryView): void {
		this.open("viewForm", state);
	}

	public openSetupDashboard(state: SetupDashboardDrawerState): void {
		this.open("setupDashboard", state);
	}

	public openAddToDashboard(state: AddToDashboardDrawerState): void {
		this.open("addToDashboard", state);
	}

	public openEditDashboardWidget(params: InitEditDashboardWidgetDrawerStateParams): void {
		this.open("setupDashboardWidget", initEditDashboardWidgetDrawerState(params));
	}

	public openCreateDashboardWidget(params: InitCreateDashboardWidgetDrawerStateParams): void {
		this.open("setupDashboardWidget", initCreateDashboardWidgetDrawerState(params));
	}

	public openDashboardVariables(state: DashboardVariablesDrawerState): void {
		this.open("dashboardVariables", state);
	}

	public openCreateDashboardVariable(params: InitSetupDashboardVariableCreateDrawerStateParams): void {
		this.open("dashboardVariable", initSetupDashboardVariableCreateDrawerState(params));
	}

	public openEditDashboardVariable(params: InitSetupDashboardVariableEditDrawerStateParams): void {
		this.open("dashboardVariable", initSetupDashboardVariableEditDrawerState(params));
	}

	public openCreateServiceTokenWidget(state: ServiceTokenDrawerState): void {
		this.open("serviceToken", state);
	}

	public openSessionById(sessionId: string): void {
		this.open("aiAssistant", { _kind: "existing", sessionId });
	}

	public openAiChat(componentIdentifier?: string): void {
		this.open("aiAssistant", { _kind: "chat", componentIdentifier });
	}
}
